import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import LandingLayout from './layouts/LandingLayout';
import LandingPage from './pages/LandingPage';
import Terms   from './pages/Terms';
import Privacy from './pages/Privacy';
import AuthLayout from './layouts/AuthLayout';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './components/UI/ConfirmDialog';
import ProtectedRoute from './components/ProtectedRoute';

// Heavy protected routes (React Flow, timeline editor, mp4-muxer, etc.) —
// lazy-loaded so the landing page bundle stays tight.
const MediaProduction = lazy(() => import('./pages/MediaProduction'));
const MediaLab        = lazy(() => import('./pages/MediaLab'));
const Objects         = lazy(() => import('./pages/Objects'));
const ProjectMedia    = lazy(() => import('./pages/ProjectMedia'));
const Projects        = lazy(() => import('./pages/Projects'));
const Settings        = lazy(() => import('./pages/Settings'));
const Account         = lazy(() => import('./pages/Account'));
const PromptEnhancementStats = lazy(() => import('./pages/admin/PromptEnhancementStats'));
const NotFound        = lazy(() => import('./pages/NotFound'));

// Auth pages — lazy because landing → signup is the more common first hop
// than direct navigation to /login.
const Login           = lazy(() => import('./pages/Auth/Login'));
const Signup          = lazy(() => import('./pages/Auth/Signup'));
const ForgotPassword  = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword   = lazy(() => import('./pages/Auth/ResetPassword'));

import { AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// Loading fallback for lazy-loaded routes — uses the shared HelixLoader so
// every surface that says "I'm working" looks the same.
import HelixLoader from './components/UI/HelixLoader';
// SVG distortion filter for LiquidGlass surfaces. Mounted once at the app
// root so any glass component anywhere in the tree (landing, app chrome,
// modals, storyboard overlays) can reference url(#helix-glass-distortion)
// without each route having to remember to mount the filter itself.
import { LiquidGlassFilter } from './components/UI/LiquidGlass';
function RouteLoading() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#050510]">
            <HelixLoader size="xl" />
        </div>
    );
}

function AnimatedRoutes() {
    const location = useLocation();

    return (
        <AnimatePresence mode="wait">
          <Suspense fallback={<RouteLoading />}>
            <Routes location={location} key={location.pathname}>
                {/* Public landing + legal pages */}
                <Route element={<LandingLayout />}>
                    <Route index              element={<LandingPage />} />
                    <Route path="/terms"      element={<Terms />}       />
                    <Route path="/privacy"    element={<Privacy />}     />
                </Route>

                {/* Public auth routes */}
                <Route element={<AuthLayout />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                </Route>

                {/* Protected app routes — pathless layout so the LandingLayout's
                     index route owns "/". Earlier this had path="/" which
                     out-specificity'd the index match, kicking unauthenticated
                     visitors to /login from helix.eternium.ai itself. */}
                <Route
                    element={
                        <ProtectedRoute>
                            <MainLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="storyboard/*" element={<MediaProduction />} />
                    <Route path="media/*" element={<MediaProduction />} />{/* legacy alias */}
                    <Route path="media-lab" element={<MediaLab />} />
                    <Route path="objects" element={<Objects />} />
                    <Route path="project-media" element={<ProjectMedia />} />
                    <Route path="projects" element={<Projects />} />
                    {/* <Route path="chat" element={<ChatInterface />} /> */}{/* Hidden for MVP */}
                    <Route path="settings" element={<Settings />} />
                    <Route path="account" element={<Account />} />
                    <Route path="admin/prompt-enhancement" element={<PromptEnhancementStats />} />
                </Route>

                {/* 404 catch-all */}
                <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppProvider>
                <ConfirmProvider>
                    <LiquidGlassFilter />
                    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <AnimatedRoutes />
                    </Router>
                </ConfirmProvider>
            </AppProvider>
        </AuthProvider>
    );
}

export default App;
